import React from 'react';
import { Link } from 'react-router-dom';
import { BUDGET_SIGNUP_URL } from '../signup/startIntent';
import { DRINKS, DrinkKey, GLASSES_PER_BOTTLE, MomentKey } from './drinksModel';
import { useDrinksCalculator } from './useDrinksCalculator';

// Colonnes (événements) et lignes (boissons) du tableau
const EVENTS: Array<{ key: MomentKey; label: string; hint: string }> = [
  { key: 'cocktail', label: 'Vin d’honneur', hint: 'Après la cérémonie' },
  { key: 'diner', label: 'Dîner', hint: 'Repas, dessert, soirée' },
  { key: 'brunch', label: 'Brunch', hint: 'Le lendemain' },
];
const ORDER: DrinkKey[] = ['champagne', 'blanc', 'rose', 'rouge'];
const drinkOf = (key: DrinkKey) => DRINKS.find((d) => d.key === key)!;

const num = (n: number) => n.toLocaleString('fr-FR', { maximumFractionDigits: 1 });
const plural = (n: number, word: string) => `${word}${n > 1 ? 's' : ''}`;

// Calculateur de boissons : un tableau événements × boissons. On coche ce qui est
// servi ; les verres par personne sont fixés (affichés, non modifiables). Il se lit
// par événement (dernière ligne) comme par boisson (colonne de droite).
const DrinksCalculator: React.FC = () => {
  const c = useDrinksCalculator();
  const { settings: s, result: r } = c;
  const bottlesAt = (m: MomentKey, d: DrinkKey) => r.moments.find((x) => x.key === m)?.bottles[d] ?? 0;
  const eventTotal = (m: MomentKey) => ORDER.reduce((n, d) => n + bottlesAt(m, d), 0);
  const eventGlasses = (m: MomentKey) =>
    ORDER.reduce((n, d) => n + (s.moments[m].served[d] ? s.moments[m].glasses[d] : 0), 0);

  return (
    <div className="mg-calc">
      <div className="mg-calc-head">
        <div className="mg-calc-title">
          <h2>Que servez-vous, et quand ?</h2>
          <p>Touchez une case : 1 ou 2 verres par personne, puis « pas servi ».</p>
        </div>
        <div className="mg-calc-guests">
          <button type="button" onClick={() => c.setGuests(s.guests - 5)} aria-label="5 invités de moins">
            −
          </button>
          <input
            type="number"
            min={0}
            inputMode="numeric"
            aria-label="Nombre d’invités adultes"
            value={s.guests || ''}
            onChange={(e) => c.setGuests(Number(e.target.value))}
          />
          <button type="button" onClick={() => c.setGuests(s.guests + 5)} aria-label="5 invités de plus">
            +
          </button>
          <span>invités adultes</span>
        </div>
      </div>

      <table className="mg-calc-table">
        <thead>
          <tr>
            <th scope="col">
              <span className="sr-only">Boisson</span>
            </th>
            {EVENTS.map((e) => (
              <th key={e.key} scope="col">
                <span className="mg-calc-event">{e.label}</span>
                <span className="mg-calc-hint">{e.hint}</span>
              </th>
            ))}
            <th scope="col" className="mg-calc-right">
              Bouteilles
            </th>
          </tr>
        </thead>
        <tbody>
          {ORDER.map((d) => {
            const drink = drinkOf(d);
            const n = r.totals[d];
            const cartons = Math.ceil(n / 6);
            return (
              <tr key={d} style={{ '--drink': drink.color } as React.CSSProperties}>
                <th scope="row">
                  <span className="mg-calc-drink">
                    <span className="mg-calc-dot" aria-hidden="true" />
                    {drink.label}
                  </span>
                  <span className="mg-calc-drink-total">
                    {n ? `${n} btl. · ${cartons} ${plural(cartons, 'carton')}` : 'Pas servi'}
                  </span>
                </th>
                {EVENTS.map((e) => {
                  const on = s.moments[e.key].served[d];
                  const g = s.moments[e.key].glasses[d];
                  return (
                    <td key={e.key}>
                      <button
                        type="button"
                        className={`mg-calc-cell${on ? ' is-on' : ''}`}
                        aria-pressed={on}
                        aria-label={`${drink.label}, ${e.label} : ${on ? `${num(g)} ${plural(g, 'verre')} par personne` : 'pas servi'}`}
                        onClick={() => c.cycleGlasses(e.key, d)}
                      >
                        <span className="mg-calc-check" aria-hidden="true">
                          {on ? '✓' : ''}
                        </span>
                        <span className="mg-calc-glasses">{on ? `${num(g)} ${plural(g, 'verre')}` : '—'}</span>
                      </button>
                    </td>
                  );
                })}
                <td className="mg-calc-right">
                  <b>{n || '—'}</b>
                  {n > 0 && (
                    <small>
                      {cartons} {plural(cartons, 'carton')}
                    </small>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Par événement</th>
            {EVENTS.map((e) => {
              const t = eventTotal(e.key);
              const g = eventGlasses(e.key);
              return (
                <td key={e.key}>
                  <span className={t ? 'mg-calc-event-total' : 'mg-calc-event-total is-empty'}>
                    {t ? `${t} ${plural(t, 'bouteille')}` : 'Rien de prévu'}
                  </span>
                  {g > 0 && (
                    <small>
                      {num(g)} {plural(g, 'verre')} / pers.
                    </small>
                  )}
                </td>
              );
            })}
            <td className="mg-calc-right" />
          </tr>
        </tfoot>
      </table>

      <div className="mg-calc-foot" aria-live="polite">
        <p className="mg-calc-total">
          <b>{r.total}</b> {plural(r.total, 'bouteille')} au total
        </p>
        <Link to={BUDGET_SIGNUP_URL} className="mkt-btn primary">
          Ajouter à mon budget
        </Link>
      </div>
      <p className="mg-calc-note">
        Bouteilles de 75 cl, {GLASSES_PER_BOTTLE} verres par bouteille, {s.margin} % de marge comprise. Prévoyez aussi
        l’eau et les softs.
      </p>
    </div>
  );
};

export default DrinksCalculator;
